import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { knowledgeCategories, knowledgeFormats, legalDisclaimer } from '../siteContent';
import { useSiteContent } from '../context/SiteContentContext';
import { isAuthenticated } from '../utils/auth';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const ArticleSkeleton = () => (
  <div className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden">
    <div className="h-44 bg-[#E5E7EB] animate-pulse" />
    <div className="p-6 space-y-3">
      <div className="h-4 w-20 bg-[#E5E7EB] animate-pulse rounded-full" />
      <div className="h-5 bg-[#E5E7EB] animate-pulse rounded-lg" />
      <div className="h-4 bg-[#E5E7EB] animate-pulse rounded-lg w-3/4" />
      <div className="h-4 bg-[#E5E7EB] animate-pulse rounded-lg" />
      <div className="h-4 bg-[#E5E7EB] animate-pulse rounded-lg w-5/6" />
      <div className="h-9 bg-[#E5E7EB] animate-pulse rounded-full mt-4" />
    </div>
  </div>
);

const KnowledgeHub = () => {
  const { knowledgehub: c } = useSiteContent();
  const [articles, setArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = isAuthenticated();

  const fetchArticles = (category) => {
    setLoading(true);
    const url = category ? `${API}/articles/?category=${encodeURIComponent(category)}` : `${API}/articles/`;
    axios.get(url)
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchArticles(null); }, []);

  const handleCategory = (cat) => {
    const next = activeCategory === cat ? null : cat;
    setActiveCategory(next);
    fetchArticles(next);
    document.getElementById('articles')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#FDFBF9]" data-testid="knowledge-hub-page">
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.hero.eyebrow}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A]">
            {c.hero.heading}
          </h1>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">{c.hero.subtext}</p>
        </div>
      </section>

      {/* Categories */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {knowledgeCategories.map((cat) => (
              <button
                key={cat.title}
                onClick={() => handleCategory(cat.title)}
                className={`text-left bg-white rounded-3xl border p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${activeCategory === cat.title ? 'border-[#E74C3C] ring-1 ring-[#E74C3C]' : 'border-[#EAE5E0]'}`}
                data-testid={`category-${cat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#003366]/5 flex items-center justify-center mb-6">
                  <Icon name={cat.icon} className="w-7 h-7 text-[#003366]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A]">{cat.title}</h3>
                <p className="text-sm text-[#666666] mt-2">{cat.count} resources</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section id="articles" className="py-16 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
              {activeCategory ? `${activeCategory} articles` : 'Latest articles'}
            </h2>
            {activeCategory && (
              <button onClick={() => handleCategory(activeCategory)} className="text-sm font-medium text-[#E74C3C]">Clear filter</button>
            )}
          </div>

          {/* Auth banner for guests */}
          {!auth && (
            <div className="mb-8 rounded-2xl bg-[#003366] text-white p-6 flex flex-col sm:flex-row items-center gap-4 justify-between" data-testid="auth-banner">
              <div>
                <p className="font-heading font-bold text-lg">Members get full access to every article</p>
                <p className="text-white/70 text-sm mt-1">Join free to read any article in full.</p>
              </div>
              <Link to="/membership" className="flex-shrink-0">
                <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-6">
                  Join Free to Access <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((n) => <ArticleSkeleton key={n} />)}
            </div>
          ) : articles.length === 0 ? (
            <p className="text-[#666666]" data-testid="no-articles">No articles in this category yet — new resources are added every week.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/knowledge-hub/${a.id}`}
                  className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                  data-testid="article-card"
                >
                  {/* Cover image */}
                  <div className="h-44 bg-[#003366]/5 flex-shrink-0 overflow-hidden">
                    {a.cover_image
                      ? <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-[#003366]/20" />
                        </div>
                      )
                    }
                  </div>
                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#E74C3C] bg-[#E74C3C]/10 rounded-full px-2.5 py-0.5 mb-3 self-start">{a.category}</span>
                    <h3 className="font-heading text-lg font-bold text-[#1A1A1A] leading-snug line-clamp-2">{a.title}</h3>
                    <p className="text-sm text-[#666666] mt-2.5 line-clamp-2 flex-1">{a.excerpt}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#EAE5E0]">
                      <span className="text-xs text-[#999]">{formatDate(a.created_at)}</span>
                      <span className="text-xs text-[#999]">3 min read</span>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#003366]">
                      {auth ? 'Read article' : 'Preview'} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Formats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-10 text-center">{c.formatsHeading}</h2>
          <div className="flex flex-wrap justify-center gap-6">
            {knowledgeFormats.map((f) => (
              <div key={f.title} className="flex flex-col items-center gap-3 bg-[#FDFBF9] rounded-2xl border border-[#EAE5E0] px-8 py-6 min-w-[150px]">
                <div className="w-12 h-12 rounded-full bg-[#E74C3C]/10 flex items-center justify-center">
                  <Icon name={f.icon} className="w-6 h-6 text-[#E74C3C]" />
                </div>
                <span className="font-heading font-semibold text-[#1A1A1A]">{f.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A]">{c.bottomCta.heading}</h2>
          <Link to="/membership">
            <Button className="mt-8 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-10 py-6 text-base group">
              {c.bottomCta.button}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="mt-10 text-xs text-[#666666] leading-relaxed max-w-3xl mx-auto">{legalDisclaimer}</p>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeHub;
